import React, { useEffect, useRef, useState } from "react";
import { useTranslate } from "@obsbot/remo-i18n";
import { filterInputValueForNumber } from "@obsbot/remo-utils";
import { CustomModal } from "../CustomModal";
import { InputWithCode, InputWithCodeRef } from "../InputWithCode";
import { withMessage } from "../../HOC/withMessage";
import "./index.less";

export enum VerifyType {
    Account = "account",
    Identification = "identification",
    ForgetPass = "forgetPass",
}

export interface AccountVerifyModalProps {
    visible: boolean;
    account?: string;
    isCodeError: boolean;
    type: VerifyType;
    onGetCode: () => Promise<boolean>;
    onNext: (code: string) => void;
    setIsCodeError: (flag: boolean) => void;
    setVisible: (flag: boolean) => void;
}

export const AccountVerifyModal: React.FC<AccountVerifyModalProps> = withMessage(
    ({
        visible,
        account,
        isCodeError,
        setIsCodeError,
        setVisible,
        onGetCode,
        onNext,
        message,
        type,
    }) => {
        const t = useTranslate();
        // 验证码input
        const [code, setCode] = useState("");
        const inputRef = useRef<InputWithCodeRef | null>(null);
        // 重置
        useEffect(() => {
            inputRef.current?.reset();
            setCode("");
        }, [visible]);
        const handleOnNext = (): void => {
            if (!code) {
                message.warning(t("noInputSingle", { text1: t("verifyCode") }));
                return;
            }
            if (code.length < 6) {
                message.warning(t("identificationVerify.atLeastSix"));
                return;
            }
            onNext && onNext(code);
        };
        return (
            <CustomModal
                className="AccountVerifyModal"
                footer={
                    <div className="verify-modal-button-group">
                        <div
                            className="normal-button"
                            onClick={() => {
                                setCode("");
                                setVisible && setVisible(false);
                            }}
                        >
                            {type === VerifyType.Account || type === VerifyType.ForgetPass
                                ? t("cancel")
                                : t("identificationVerify.cancel")}
                        </div>
                        <div
                            className={`verify-modal-button-item ${type === VerifyType.Identification ? "button-danger" : ""}`}
                            onClick={handleOnNext}
                        >
                            {type === VerifyType.Account || type === VerifyType.ForgetPass
                                ? t("nextStep")
                                : t("identificationVerify.confirm")}
                        </div>
                    </div>
                }
                open={visible}
                renderHeader={
                    type === VerifyType.Account || type === VerifyType.ForgetPass
                        ? t("accountVerify")
                        : t("identificationVerify.title")
                }
                setVisible={setVisible}
                width={392}
                onClickClose={() => setCode("")}
            >
                <div className="verify-modal-warn-text">
                    {type === VerifyType.Account || type === VerifyType.ForgetPass
                        ? account?.includes("@")
                            ? t("verifyEmail", { email: account })
                            : t("verifyPhone", { phone: account })
                        : t("identificationVerify.description")}
                </div>
                <div className="verify-modal-code">
                    <div className="verify-modal-code-title">{t("verifyCode")}</div>
                    <InputWithCode
                        ref={inputRef}
                        className="verify-modal-code-input"
                        code={code}
                        errorMsg={t("verifyFailed")}
                        isCodeError={isCodeError}
                        maxLength={6}
                        placeholder={t("verifyCodePlaceholder")}
                        setCode={setCode}
                        t={t}
                        value={code}
                        onChange={e => {
                            setCode(filterInputValueForNumber(e.target.value));
                            setIsCodeError && setIsCodeError(false);
                        }}
                        onFocus={() => setIsCodeError && setIsCodeError(false)}
                        onGetCode={onGetCode}
                        onKeyDown={e => {
                            if (e.key === "Enter") {
                                handleOnNext();
                            }
                        }}
                    />
                </div>
            </CustomModal>
        );
    },
);
